export type WechatFlow = 'sign-in' | 'binding';
export type WechatNotice = 'unavailable' | 'expired' | 'conflict' | 'cancelled' | 'popup-blocked';
export type WechatStep = 'account-proof' | 'sign-in-qr' | 'binding-qr' | 'error';
export type WechatNavigation = {
  revision: 1;
  step: WechatStep;
  notice?: WechatNotice;
};

export type WechatReference = {
  revision: 1;
  connectorId: string;
  state: string;
  verificationId: string;
  flow: WechatFlow;
};

export const identityOrigin = 'https://test.id.vibehub.pub';
export const referenceKey = 'vibehub.identity.wechat-reference.v1';
export const callbackMessage = 'vibehub.identity.wechat-callback.v1';
export const opaque = (value: unknown, minimum = 1, maximum = 128): value is string =>
  typeof value === 'string' &&
  value.length >= minimum &&
  value.length <= maximum &&
  /^[\w-]+$/.test(value);
export const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const exact = (value: Record<string, unknown>, names: readonly string[]) =>
  Object.keys(value).length === names.length && names.every((name) => Object.hasOwn(value, name));
const notices = new Set<unknown>([
  'unavailable',
  'expired',
  'conflict',
  'cancelled',
  'popup-blocked',
]);

const isNotice = (value: unknown): value is WechatNotice => notices.has(value);
const isStep = (value: unknown): value is WechatStep =>
  value === 'account-proof' ||
  value === 'sign-in-qr' ||
  value === 'binding-qr' ||
  value === 'error';

/** Navigation state selects presentation only. Native records remain the proof. */
export const readWechatNavigation = (state: unknown): WechatNavigation | undefined => {
  if (!object(state) || !object(state.vibehubWechat)) {
    return;
  }
  const value = state.vibehubWechat;
  if (
    value.revision !== 1 ||
    !isStep(value.step) ||
    Object.keys(value).some((key) => !['revision', 'step', 'notice'].includes(key)) ||
    (value.notice !== undefined && !isNotice(value.notice))
  ) {
    return;
  }
  return {
    revision: 1,
    step: value.step,
    ...(value.notice === undefined ? {} : { notice: value.notice }),
  };
};

/** This consumes an original same-origin HTTP error, never a browser message. */
export function nativeWechatProgress(value: unknown, status: number): WechatNavigation | undefined {
  if (!object(value) || value.code !== 'session.verification_failed' || !object(value.data)) {
    return;
  }
  const { data } = value;
  if (
    status === 403 &&
    exact(data, ['wechat_binding_state']) &&
    data.wechat_binding_state === 'awaiting_wechat'
  ) {
    return { revision: 1, step: 'binding-qr' };
  }
  if (!exact(data, ['wechat_binding_error'])) {
    return;
  }
  const errorNotices: Readonly<Record<string, WechatNotice | undefined>> = {
    wechat_attempt_expired: 'expired',
    wechat_target_changed: 'conflict',
    wechat_proof_invalid: 'expired',
    wechat_recent_auth_required: 'expired',
    wechat_alternative_login_required: 'conflict',
    wechat_unavailable: 'unavailable',
  };
  const notice = errorNotices[String(data.wechat_binding_error)];
  return notice && [400, 403, 409, 410, 503].includes(status)
    ? { revision: 1, step: 'error', notice }
    : undefined;
}

export function readWechatReference(raw: unknown): WechatReference | undefined {
  if (typeof raw !== 'string' || !raw || raw.length > 1200) {
    return;
  }
  try {
    const value: unknown = JSON.parse(raw);
    if (
      object(value) &&
      exact(value, ['revision', 'connectorId', 'state', 'verificationId', 'flow']) &&
      value.revision === 1 &&
      opaque(value.connectorId) &&
      opaque(value.state, 16, 512) &&
      opaque(value.verificationId) &&
      (value.flow === 'sign-in' || value.flow === 'binding')
    ) {
      return {
        revision: 1,
        connectorId: value.connectorId,
        state: value.state,
        verificationId: value.verificationId,
        flow: value.flow,
      };
    }
  } catch {
    /* Corrupt hints cannot become proof. */
  }
}

/** Official URL control data stays outside the React view model. */
export function officialAuthorization(
  value: unknown,
  connectorId: string,
  state: string
): string | undefined {
  if (
    !opaque(connectorId) ||
    !opaque(state, 16, 512) ||
    typeof value !== 'string' ||
    value.length > 8192
  ) {
    return;
  }
  try {
    const url = new URL(value);
    const names = [...url.searchParams.keys()];
    if (
      url.origin !== 'https://open.weixin.qq.com' ||
      url.pathname !== '/connect/qrconnect' ||
      url.username ||
      url.password ||
      url.hash !== '#wechat_redirect' ||
      names.length !== 5 ||
      new Set(names).size !== 5 ||
      names.some(
        (name) => !['appid', 'redirect_uri', 'response_type', 'scope', 'state'].includes(name)
      ) ||
      !opaque(url.searchParams.get('appid'), 4) ||
      url.searchParams.get('response_type') !== 'code' ||
      url.searchParams.get('scope') !== 'snsapi_login' ||
      url.searchParams.get('state') !== state ||
      url.searchParams.get('redirect_uri') !== `${identityOrigin}/callback/${connectorId}`
    ) {
      return;
    }
    return url.href;
  } catch {
    /* An invalid provider URL is not opened. */
  }
}

export function officialFrameURL(
  authorization: string,
  reference: WechatReference
): string | undefined {
  const trusted = officialAuthorization(authorization, reference.connectorId, reference.state);
  if (!trusted) {
    return;
  }
  try {
    const url = new URL(trusted);
    // Only this freshly parsed local URL is modified.
    // eslint-disable-next-line @silverhand/fp/no-mutation
    url.hash = '';
    url.searchParams.set('login_type', 'jssdk');
    url.searchParams.set('self_redirect', 'true');
    url.searchParams.set('color_scheme', 'light');
    return url.href;
  } catch {
    /* Only the validated original authorization is framed. */
  }
}

export function callbackAddress(value: string, reference: WechatReference): string | undefined {
  try {
    const url = new URL(value);
    const names = [...url.searchParams.keys()];
    if (
      url.origin !== identityOrigin ||
      url.pathname !== `/callback/${reference.connectorId}` ||
      url.username ||
      url.password ||
      url.hash ||
      names.some((name) => !['code', 'state'].includes(name)) ||
      new Set(names).size !== names.length ||
      url.searchParams.get('state') !== reference.state ||
      (url.searchParams.has('code') && !opaque(url.searchParams.get('code'), 1, 512))
    ) {
      return;
    }
    return url.href;
  } catch {
    /* A hint cannot choose an arbitrary identity navigation. */
  }
}
